import { Fragment, useCallback, useEffect, useRef } from "react";
import { Divider, Box } from "@mui/material";

import VideoPlayerControls from "./VideoPlayerControls/VideoPlayerControls";
import { useAppContext } from "../hooks/useAppContext";

// 1. Import the Autonomous Brain
import { useKernel } from "../hooks/AIKernel";

type TVideoPlayerProps = {
  videoSrc: string;
};

const VideoPlayer: React.FC<TVideoPlayerProps> = ({ videoSrc }) => {
  const { setVideoRef, videoRef } = useAppContext();
  
  // 2. Connect to the Agent's state and sensory inputs
  const { agentState, reportBehavior } = useKernel();

  // Refs for behavioral tracking
  const playToggles = useRef<number[]>([]);
  const playStartTime = useRef<number>(0);

  const handlePlayPause = useCallback(
    (e?: React.MouseEvent | KeyboardEvent) => {
      e?.preventDefault();
      if (!videoRef) return;

      // TELEMETRY: Micro-Frustration Tracking (Frame-hunting)
      const now = Date.now();
      playToggles.current.push(now);
      
      // Keep only toggles from the last 2.5 seconds
      playToggles.current = playToggles.current.filter(t => now - t < 2500);

      // If they toggle 5 times in 2.5 seconds, they are struggling to find a frame
      if (playToggles.current.length >= 5) {
        if (reportBehavior) reportBehavior("video_scrubbing_frustration", 2, "behavior");
        playToggles.current = []; // Reset after reporting
      }

      videoRef.paused ? videoRef.play() : videoRef.pause();
    },
    [videoRef, reportBehavior]
  );

  // TELEMETRY: Deep Focus / Watch Time Tracking
  const handlePlay = useCallback(() => {
    playStartTime.current = Date.now();
  }, []);

  const handlePause = useCallback(() => {
    if (playStartTime.current > 0) {
      const watchedSeconds = (Date.now() - playStartTime.current) / 1000;
      
      // If they watched uninterrupted for more than 5 seconds, log it as high interest
      if (watchedSeconds > 5 && reportBehavior) {
        reportBehavior("video_playback_focus", watchedSeconds * 0.5, "interest");
      }
      playStartTime.current = 0;
    }
  }, [reportBehavior]);

  useEffect(() => {
    const playPauseOnSpacePress = (event: KeyboardEvent) => {
      // Prevent spacebar scrolling and trigger play/pause
      if (event.key === " " && document.activeElement?.tagName !== "INPUT") {
        handlePlayPause(event);
      }
    };

    window.addEventListener("keydown", playPauseOnSpacePress);
    return () => window.removeEventListener("keydown", playPauseOnSpacePress);
  }, [handlePlayPause]);

  // ADAPTIVE UI: Evaluate the current theme to apply cinematic styling
  const isDarkMode = agentState?.theme === "theme-cinematic-dark";

  return (
    <Fragment>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          marginBottom: "20px",
          borderRadius: "8px",
          overflow: "hidden",
          background: "#000",
          // AI Actuator: Add an immersive ambient glow if in dark mode
          boxShadow: isDarkMode ? "0px 10px 40px rgba(0, 229, 255, 0.15)" : "none",
          transition: "box-shadow 0.5s ease",
        }}
      >
        <video
          playsInline
          onClick={handlePlayPause}
          onPlay={handlePlay}
          onPause={handlePause}
          ref={setVideoRef}
          src={videoSrc}
          style={{ width: "100%", display: "block" }}
        />
      </Box>
      <Divider sx={{ borderColor: isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)" }} />
      <VideoPlayerControls />
    </Fragment>
  );
};

export default VideoPlayer;